import React, { useMemo, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import "./SIPCalculator.css";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Number(Math.round(n || 0))
  );

export default function SIPCalculator({ onBack }) {
  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);
  const [annualIncrease, setAnnualIncrease] = useState(10);

  const results = useMemo(() => {
    const baseAmount = Number(amount) || 0;
    const safeYears = Number(years) || 0;
    const safeRate = Number(rate) || 0;
    const safeAnnualIncrease = Number(annualIncrease) || 0;

    const monthlyRate = safeRate / 100 / 12;
    let futureValue = 0;
    let totalInvested = 0;

    for (let year = 0; year < safeYears; year++) {
      const monthlyInvestment =
        baseAmount * Math.pow(1 + safeAnnualIncrease / 100, year);

      for (let month = 0; month < 12; month++) {
        const monthsRemaining = (safeYears - year) * 12 - month;
        const fv = monthlyInvestment * Math.pow(1 + monthlyRate, monthsRemaining);
        futureValue += fv;
        totalInvested += monthlyInvestment;
      }
    }

    const returns = futureValue - totalInvested;
    return { invested: totalInvested, returns, future_value: futureValue };
  }, [amount, annualIncrease, rate, years]);

  // Generate chart data
  const chartData = useMemo(() => {
    const baseAmount = Number(amount) || 0;
    const safeYears = Number(years) || 0;
    const safeRate = Number(rate) || 0;
    const safeAnnualIncrease = Number(annualIncrease) || 0;
    const monthlyRate = safeRate / 100 / 12;

    const data = [{ name: 'Start', invested: 0, value: 0 }];
    let cumInvested = 0;
    let cumValue = 0;

    for (let year = 0; year < safeYears; year++) {
      const monthlyInvestment = baseAmount * Math.pow(1 + safeAnnualIncrease / 100, year);
      for (let month = 0; month < 12; month++) {
        cumInvested += monthlyInvestment;
        cumValue = (cumValue + monthlyInvestment) * (1 + monthlyRate);
      }

      data.push({
        name: `${year + 1}Y`,
        invested: Math.round(cumInvested),
        value: Math.round(cumValue),
      });
    }
    return data;
  }, [amount, annualIncrease, rate, years]);

  const rangeBg = (value, min, max) => {
    const pct = ((value - min) / (max - min)) * 100;
    return `linear-gradient(90deg, #7c3aed ${pct}%, rgba(255,255,255,0.06) ${pct}%)`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="sip-chart-tooltip">
          <p className="sip-tooltip-label">{label}</p>
          <p className="sip-tooltip-val" style={{ color: '#10b981' }}>Value: ₹ {formatINR(payload[1]?.value)}</p>
          <p className="sip-tooltip-val" style={{ color: '#a855f7' }}>Invested: ₹ {formatINR(payload[0]?.value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="sip-page">
      <div className="sip-inner">
        {/* Back + Title */}
        <button className="sip-back-btn" onClick={onBack}>← Back to Dashboard</button>

        <h1 className="sip-title">
          SIP <span className="sip-gradient-text">Calculator</span>
        </h1>
        <p className="sip-desc">
          The SIP calculator helps estimate the potential growth of your Systematic Investment Plan.<br />
          Plan your long-term financial goals with precision and confidence.
        </p>

        <div className="sip-grid">
          {/* LEFT — Input Card */}
          <div className="sip-card sip-left">
            <h3 className="sip-card-heading">
              <span className="sip-card-icon">📊</span> Returns Estimator
            </h3>
            <p className="sip-card-sub">Estimation is based on the past performance</p>

            <label className="sip-field-label">Monthly SIP Amount <span className="sip-info-icon">ⓘ</span></label>
            <div className="sip-amount-box">
              <span className="sip-rupee">₹</span>
              <input
                type="text"
                className="sip-amount-input"
                value={formatINR(amount)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setAmount(raw === '' ? '' : Number(raw));
                }}
              />
            </div>

            <div className="sip-control">
              <label className="sip-control-label">
                Select Duration <span className="sip-info-icon">ⓘ</span>
                <span className="sip-control-value">
                  <input
                    type="number"
                    className="sip-inline-input"
                    value={years}
                    onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
                    onBlur={() => {
                      let val = Number(years);
                      if (val < 1) val = 1;
                      if (val > 30) val = 30;
                      setYears(val);
                    }}
                  /> Yr
                </span>
              </label>
              <input
                type="range" min="1" max="30" step="1"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                style={{ background: rangeBg(years, 1, 30) }}
              />
              <div className="sip-minmax"><span>1 Yr</span><span>30 Yr</span></div>
            </div>

            <div className="sip-control">
              <label className="sip-control-label">
                Expected Rate of Return (P.A.) <span className="sip-info-icon">ⓘ</span>
                <span className="sip-control-value">
                  <input
                    type="number"
                    className="sip-inline-input"
                    value={rate}
                    onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
                    onBlur={() => {
                      let val = Number(rate);
                      if (val < 1) val = 1;
                      if (val > 30) val = 30;
                      setRate(val);
                    }}
                  /> %
                </span>
              </label>
              <input
                type="range" min="1" max="30" step="0.5"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                style={{ background: rangeBg(rate, 1, 30) }}
              />
              <div className="sip-minmax"><span>1%</span><span>30%</span></div>
            </div>

            <div className="sip-control">
              <label className="sip-control-label">
                Annual Increase <span className="sip-info-icon">ⓘ</span>
                <span className="sip-control-value">
                  <input
                    type="number"
                    className="sip-inline-input"
                    value={annualIncrease}
                    onChange={(e) => setAnnualIncrease(e.target.value === '' ? '' : Number(e.target.value))}
                    onBlur={() => {
                      let val = Number(annualIncrease);
                      if (val < 0) val = 0;
                      if (val > 30) val = 30;
                      setAnnualIncrease(val);
                    }}
                  /> %
                </span>
              </label>
              <input
                type="range" min="0" max="30" step="1"
                value={annualIncrease}
                onChange={(e) => setAnnualIncrease(Number(e.target.value))}
                style={{ background: rangeBg(annualIncrease, 0, 30) }}
              />
              <div className="sip-minmax"><span>0%</span><span>30%</span></div>
            </div>

            <div className="sip-disclaimer">
              <span className="sip-disclaimer-icon">ⓘ</span>
              <p>All calculations are based on the assumptions you provide. Results are indicative and may vary with market performance.</p>
            </div>
          </div>

          {/* RIGHT — Results Card */}
          <div className="sip-card sip-right">
            <h3 className="sip-card-heading">
              <span className="sip-card-icon">📈</span> Your SIP Plan Summary
            </h3>

            {/* Summary Row */}
            <div className="sip-summary-row">
              <div className="sip-summary-item">
                <span className="sip-summary-label">Total Invested</span>
                <span className="sip-summary-value">₹ {formatINR(results.invested)}</span>
              </div>
              <div className="sip-summary-item">
                <span className="sip-summary-label">Est. Returns</span>
                <span className="sip-summary-value sip-val-blue">₹ {formatINR(results.returns)}</span>
              </div>
              <div className="sip-summary-item">
                <span className="sip-summary-label">Total Value</span>
                <span className="sip-summary-value sip-val-green">₹ {formatINR(results.future_value)}</span>
              </div>
            </div>

            {/* Chart */}
            <div className="sip-chart-section">
              <div className="sip-chart-header">
                <h4 className="sip-chart-title">Investment Growth <span className="sip-info-icon">ⓘ</span></h4>
                <span className="sip-chart-badge">Projected for {years} Year{years > 1 ? 's' : ''}</span>
              </div>

              <div className="sip-chart-wrap">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sipGrad1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a855f7" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="sipGrad2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} tickFormatter={(v) => v >= 10000000 ? `₹ ${(v / 10000000).toFixed(1)}Cr` : v >= 100000 ? `₹ ${(v / 100000).toFixed(1)}L` : `₹ ${formatINR(v)}`} width={70} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="invested" stroke="#a855f7" strokeWidth={2} fill="url(#sipGrad1)" />
                    <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} fill="url(#sipGrad2)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Total Value Callout */}
              <div className="sip-final-callout">
                <span className="sip-final-label">Total Value</span>
                <span className="sip-final-amount">₹ {formatINR(results.future_value)}</span>
              </div>

              {/* Legend */}
              <div className="sip-chart-legend">
                <span className="sip-legend-item"><span className="sip-legend-dot" style={{ background: '#a855f7' }} /> Amount Invested</span>
                <span className="sip-legend-item"><span className="sip-legend-dot" style={{ background: '#10b981' }} /> Total Value</span>
              </div>
            </div>

            <div className="sip-disclaimer">
              <span className="sip-disclaimer-icon">ⓘ</span>
              <p>SIP returns are not guaranteed. Actual returns may vary based on market conditions and fund performance.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
